import { FitbitSummary, readFitbitData } from '../../../services/fitbitCollectionDataService';
import { FitbitExportDateBasis } from '../../../services/fitbitExtractionService';
import { collectionHistoryRange, DateRange } from './dataAvailability';

const dayMs = 86400000;
export type StoredHistory = { summary: FitbitSummary; requestedRange: DateRange | null };

// The server accepts at most 3,660 days between the endpoints of one read.
// Split long histories into disjoint ranges instead of silently losing older data.
export function historyReadRanges(range: DateRange): DateRange[] {
  const start = Date.parse(range.startDate); const end = Date.parse(range.endDate);
  if (!Number.isFinite(start) || !Number.isFinite(end) || start > end ||
    new Date(start).toISOString().slice(0, 10) !== range.startDate || new Date(end).toISOString().slice(0, 10) !== range.endDate) {
    throw new Error('The historical collection dates are unavailable.');
  }
  const ranges: DateRange[] = [];
  for (let from = start; from <= end; from += 3661 * dayMs) {
    ranges.push({ startDate: new Date(from).toISOString().slice(0, 10),
      endDate: new Date(Math.min(end, from + 3660 * dayMs)).toISOString().slice(0, 10) });
  }
  return ranges;
}

export function storedRecordRange(summary: Pick<FitbitSummary, 'metrics'>, routeKey?: string, dateBasis: FitbitExportDateBasis = 'utc'): DateRange | null {
  const populated = summary.metrics.filter(metric => (!routeKey || metric.routeKey === routeKey) &&
    Number(metric.presentRecords) > 0 && metric.firstRecord && metric.lastRecord);
  if (!populated.length) return null;
  const date = (metric: typeof populated[number], first: boolean) => {
    const utc = (first ? metric.firstRecord! : metric.lastRecord!).slice(0, 10);
    if (dateBasis === 'utc') return utc;
    const local = first ? metric.firstLocalDate : metric.lastLocalDate;
    // A cache produced by an older backend has no local bounds. Keep a safe
    // one-day margin rather than excluding an edge reading before the refresh.
    return local || new Date(Date.parse(utc) + (first ? -1 : 1) * dayMs).toISOString().slice(0, 10);
  };
  return { startDate: populated.map(metric => date(metric, true)).sort()[0],
    endDate: populated.map(metric => date(metric, false)).sort().slice(-1)[0] };
}

export async function readStoredHistory(studyId: string, participantId: string, signal: AbortSignal,
  now = new Date(), reader: typeof readFitbitData = readFitbitData): Promise<StoredHistory> {
  const today = now.toISOString().slice(0, 10);
  const check = () => { if (signal.aborted) throw new Error('History read cancelled'); };
  check();
  // Campaign dates are returned independently of the summary's selected date range.
  const seed = await reader<FitbitSummary>(studyId, participantId, 'summary', { startDate: today, endDate: today });
  check();
  const requestedRange = collectionHistoryRange(seed, now);
  if (!requestedRange) return { summary: seed, requestedRange: null };
  if (requestedRange.startDate === today && requestedRange.endDate === today) return { summary: seed, requestedRange };
  const metrics = new Map<string, FitbitSummary['metrics'][number]>();
  const catalog = new Map(seed.catalog.map(metric => [metric.routeKey, metric]));
  const dailyCoverage: FitbitSummary['dailyCoverage'] = [];
  const days: FitbitSummary['adherence']['days'] = [];
  const checkedWindows: FitbitSummary['checkedWindows'] = [];
  for (const range of historyReadRanges(requestedRange)) {
    check();
    const part = await reader<FitbitSummary>(studyId, participantId, 'summary', range);
    check();
    part.catalog.forEach(metric => catalog.set(metric.routeKey, metric));
    for (const metric of part.metrics) {
      const previous = metrics.get(metric.routeKey);
      const first = [previous?.firstRecord, metric.firstRecord].filter((date): date is string => !!date).sort();
      const last = [previous?.lastRecord, metric.lastRecord].filter((date): date is string => !!date).sort();
      metrics.set(metric.routeKey, { routeKey: metric.routeKey,
        presentRecords: Number(previous?.presentRecords || 0) + Number(metric.presentRecords),
        absentRecords: Number(previous?.absentRecords || 0) + Number(metric.absentRecords),
        firstRecord: first[0] || null, lastRecord: last[last.length - 1] || null });
    }
    for (const row of part.dailyCoverage) dailyCoverage.push(row);
    for (const row of part.adherence.days) days.push(row);
    for (const row of part.checkedWindows) checkedWindows.push(row);
  }
  return { requestedRange, summary: { ...seed, catalog: Array.from(catalog.values()), metrics: Array.from(metrics.values()),
    dailyCoverage, checkedWindows, adherence: { ...seed.adherence, days } } };
}
